import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@codes/ui-kit/components/ui/accordion';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProductView } from '@lemoncloud/codes-goods-api';
import { Copy, ExternalLink } from 'lucide-react';

interface ProductCardStackDetailsProps {
    product: ProductView;
    isDeploying?: boolean;
    isChild?: boolean;
}

type CopyState = 'idle' | 'copying' | 'copied';

interface StackField {
    key: keyof NonNullable<ProductView['stack$']>;
    label: string;
    type: 'text' | 'link' | 'copy';
    className?: string;
}

const STACK_FIELDS: StackField[] = [
    { key: 'endpoint', label: 'Endpoint:', type: 'copy' },
    { key: 'websiteEndpoint', label: 'Website:', type: 'link' },
    { key: 'apiId', label: 'API ID:', type: 'text' },
    { key: 'identityLoginName', label: 'Identity:', type: 'text', className: 'truncate ml-2' },
    { key: 'stackName', label: 'Stack:', type: 'text', className: 'truncate ml-2' },
    { key: 's3StackName', label: 'S3 Stack:', type: 'text' },
    { key: 'region', label: 'Stack Region:', type: 'text' },
];

const useCopyToClipboard = () => {
    const { t } = useTranslation(['translation', 'box']);
    const [copyState, setCopyState] = useState<Record<string, CopyState>>({});
    const timeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});

    useEffect(() => {
        return () => {
            Object.values(timeoutRefs.current).forEach(clearTimeout);
        };
    }, []);

    const copyText = async (text: string, key: string) => {
        if (copyState[key] === 'copying') {
            return;
        }

        setCopyState(prev => ({ ...prev, [key]: 'copying' }));

        try {
            await navigator.clipboard.writeText(text);
            setCopyState(prev => ({ ...prev, [key]: 'copied' }));
            toast({
                title: t('box:stackDetails.endpoint.copy'),
                description: t('box:stackDetails.endpoint.copyDescription'),
            });
        } catch (error) {
            console.error('Copy failed:', error);
            toast({
                title: t('box:stackDetails.endpoint.copyFailed'),
                description: t('box:stackDetails.endpoint.copyFailedDescription'),
            });
        } finally {
            if (timeoutRefs.current[key]) {
                clearTimeout(timeoutRefs.current[key]);
            }

            timeoutRefs.current[key] = setTimeout(() => {
                setCopyState(prev => ({ ...prev, [key]: 'idle' }));
                delete timeoutRefs.current[key];
            }, 1000);
        }
    };

    return { copyState, copyText };
};

const checkHasStackInfo = (stack?: ProductView['stack$']): boolean => {
    if (!stack) {
        return false;
    }
    return STACK_FIELDS.some(field => Boolean(stack[field.key]));
};

const LoadingSpinner = () => (
    <div className="w-3 h-3 border border-border border-t-transparent rounded-full animate-spin flex-shrink-0" />
);

const StatusIndicator = ({ hasInfo, isDeploying }: { hasInfo: boolean; isDeploying?: boolean }) => (
    <span
        className={`w-2 h-2 rounded-full ${
            isDeploying ? 'bg-amber-500/70' : hasInfo ? 'bg-green-500/70' : 'bg-bar/60'
        }`}
    />
);

const TextField = ({ label, value, className }: { label: string; value: string; className?: string }) => (
    <div className="flex justify-between text-sm font-medium">
        <span className="w-24 text-sub">{label}</span>
        <span className={`text-text text-right ${className || ''}`}>{value}</span>
    </div>
);

const LinkField = ({ label, value, t }: { label: string; value: string; t: (key: string) => string }) => (
    <div className="flex justify-between text-sm font-medium">
        <span className="w-24 text-sub">{label}</span>
        <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-link underline flex items-center gap-1"
            title={value}
        >
            <span>{t('box:stackDetails.openSite')}</span>
            <ExternalLink size={18} />
        </a>
    </div>
);

const CopyField = ({
    label,
    value,
    copyState,
    onCopy,
}: {
    label: string;
    value: string;
    copyState: CopyState;
    onCopy: () => void;
}) => (
    <div className="flex justify-between gap-5 text-sm font-medium">
        <span className="w-24 text-sub">{label}</span>
        <button
            onClick={onCopy}
            className="text-link underline flex items-center gap-1 flex-1 justify-end truncate text-xs hover:text-link"
            title={`Copy: ${value}`}
            disabled={copyState === 'copying'}
        >
            <span className="truncate">{value}</span>
            {copyState === 'copying' ? <LoadingSpinner /> : <Copy className="w-3 h-3 flex-shrink-0" />}
        </button>
    </div>
);

export const ProductCardStackDetails = ({ product, isDeploying, isChild }: ProductCardStackDetailsProps) => {
    const { t } = useTranslation(['translation', 'box']);
    const { copyState, copyText } = useCopyToClipboard();

    if (!product.stack$) {
        return null;
    }

    const hasStackInfo = checkHasStackInfo(product.stack$);

    const renderStackField = (field: StackField) => {
        const value = product.stack$?.[field.key];
        if (!value) {
            return null;
        }

        // Hide region field for child products (already shown in parent)
        if (isChild && field.key === 'region') {
            return null;
        }

        const key = `${field.key}-${field.label}`;
        const fieldCopyState = copyState[field.key] || 'idle';

        switch (field.type) {
            case 'text':
                return <TextField key={key} label={field.label} value={String(value)} className={field.className} />;
            case 'link':
                return <LinkField key={key} label={field.label} value={String(value)} t={t} />;
            case 'copy':
                return (
                    <CopyField
                        key={key}
                        label={field.label}
                        value={String(value)}
                        copyState={fieldCopyState}
                        onCopy={() => copyText(String(value), field.key)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="stack-info" className="border-none">
                <AccordionTrigger
                    onClick={e => e.stopPropagation()}
                    className="pt-3.5 pb-0 max-xs:pt-3.5 max-xs:pb-0 !text-base border-t border-border"
                >
                    <span className="flex items-center gap-2">
                        <StatusIndicator hasInfo={hasStackInfo} isDeploying={isDeploying} />
                        {t('box:stackDetails.title')}
                    </span>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-0 max-xs:pb-0">
                    {hasStackInfo ? (
                        <div className="flex flex-col gap-2 text-sm font-medium">
                            {STACK_FIELDS.map(field => renderStackField(field))}
                        </div>
                    ) : (
                        <div className="text-text text-center py-2">{t('box:stackDetails.noInfo')}</div>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};
